/**
 * weekly-report-schema — Zod schema for the Weekly Status Report form (frontend-standards §7).
 *
 * Mirrors the server-side contract in `packages/weekly-reports/validation.ts`
 * (`CreateWeeklyReportInputSchema`). Keep the two in sync — in particular the
 * `cumulative_hours` bounds exported below.
 *
 * All fields are modelled as strings because the form is driven by native `<input>` /
 * `<Input type="number">` / `<Input type="date">` controls, which always produce strings.
 * Conversion to the API payload happens in the component's submit handler.
 */

import { z } from "zod";

import { weeklyReportMessages as msg } from "./weekly-report-messages";
import { phaseLabel, phaseNumber } from "@/lib/phases";

/** Lower bound for `cumulative_hours`. Mirrors the backend `z.number().min(0)`. */
export const CUMULATIVE_HOURS_MIN = 0;

/**
 * Upper bound for `cumulative_hours`. A project cannot plausibly log more than this, and an
 * unbounded value is almost always a typo (e.g. a pasted date or an extra digit).
 * Mirror this value in `packages/weekly-reports/validation.ts`.
 */
export const CUMULATIVE_HOURS_MAX = 100_000;

/** Optional field → blank is always allowed; the rules below only apply to a non-blank value. */
const isBlank = (value: string): boolean => value === "";

/**
 * Cumulative hours: optional, but when provided must be a plain non-negative decimal within
 * range. `<Input type="number">` still lets the user type `-5`, `1e9` or (in some browsers)
 * arbitrary text, so every rule is enforced here rather than relying on the `min`/`step` attrs.
 */
const cumulativeHoursField = z
  .string()
  .trim()
  // Not a number at all (empty-after-trim is handled by isBlank).
  .refine((v) => isBlank(v) || Number.isFinite(Number(v)), {
    message: msg.errors.cumulativeHoursInvalid,
  })
  // Negative values.
  .refine((v) => isBlank(v) || Number(v) >= CUMULATIVE_HOURS_MIN, {
    message: msg.errors.cumulativeHoursNegative,
  })
  // Out of range (typo guard).
  .refine((v) => isBlank(v) || Number(v) <= CUMULATIVE_HOURS_MAX, {
    message: msg.errors.cumulativeHoursMax,
  })
  // Reject exponent/hex/whitespace-separated forms that `Number()` happily accepts.
  .refine((v) => isBlank(v) || /^\d+(\.\d+)?$/.test(v), {
    message: msg.errors.cumulativeHoursInvalid,
  });

const escalationField = z.enum(["", "green", "red"]);
const ragField = z.enum(["", "green", "yellow", "red"]);

export const weeklyReportFormSchema = z.object({
  // Reporting period.
  week_start: z.string(),
  week_ending: z.string().min(1, msg.errors.endDateRequired),

  // Status.
  overall_status: z.enum(["on_track", "at_risk", "off_track"]),
  escalation: escalationField,
  schedule: ragField,
  scope: ragField,
  budget_status: ragField,
  cumulative_hours: cumulativeHoursField,

  // Phase completion.
  current_phase: z.string(),
  phase_p0_completed_at: z.string(),
  phase_p1_completed_at: z.string(),
  phase_p2_completed_at: z.string(),
  phase_p3_completed_at: z.string(),
  phase_p4_completed_at: z.string(),

  // Narrative.
  project_health: z.string(),
  planned_vs_actual: z.string(),
  team_did_this_week: z.string(),
  team_will_do_next: z.string(),
  risks: z.string(),
  decisions: z.string(),
});

export type WeeklyReportFormValues = z.infer<typeof weeklyReportFormSchema>;

/** Blank form state — also used to reset the form after a successful submit. */
export const weeklyReportFormDefaults: WeeklyReportFormValues = {
  week_start: "",
  week_ending: "",
  overall_status: "on_track",
  escalation: "",
  schedule: "",
  scope: "",
  budget_status: "",
  cumulative_hours: "",
  current_phase: "",
  phase_p0_completed_at: "",
  phase_p1_completed_at: "",
  phase_p2_completed_at: "",
  phase_p3_completed_at: "",
  phase_p4_completed_at: "",
  project_health: "",
  planned_vs_actual: "",
  team_did_this_week: "",
  team_will_do_next: "",
  risks: "",
  decisions: "",
};

/**
 * "Save as Draft" schema (V047). A draft needs only an end date; every other field may be blank.
 * Field-format rules that would otherwise make the SERVER reject the draft (cumulative hours) are
 * still enforced, so a draft can never be saved with a value that fails later for a format reason.
 * Mirrors `status: 'draft'` in packages/weekly-reports/validation.ts.
 */
export const weeklyReportDraftSchema = weeklyReportFormSchema
  .partial()
  .required({ week_ending: true })
  .extend({
    week_ending: z.string().min(1, msg.errors.endDateRequired),
    cumulative_hours: cumulativeHoursField.optional(),
  });

/** Minimal shape of a stored report that the form can be pre-filled from. */
export interface PrefillableReport {
  week_start: string | null;
  week_ending: string;
  overall_status: WeeklyReportFormValues["overall_status"] | null;
  escalation: string | null;
  schedule: string | null;
  scope: string | null;
  budget_status: string | null;
  cumulative_hours: number | null;
  current_phase: string | null;
  phase_p0_completed_at: string | null;
  phase_p1_completed_at: string | null;
  phase_p2_completed_at: string | null;
  phase_p3_completed_at: string | null;
  phase_p4_completed_at: string | null;
  project_health: string | null;
  planned_vs_actual: string | null;
  team_did_this_week: string | null;
  team_will_do_next: string | null;
  risks: string | null;
  decisions: string | null;
}

const dateValue = (v: string | null | undefined): string =>
  v ? v.slice(0, 10) : "";
const textValue = (v: string | null | undefined): string => v ?? "";

function enumValue<T extends string>(
  v: string | null | undefined,
  allowed: readonly T[],
): T | "" {
  return v && (allowed as readonly string[]).includes(v) ? (v as T) : "";
}

/**
 * Map a stored report onto form values so "Continue editing" / "Edit" open with every saved field
 * in place. Unknown/NULL values fall back to the blank defaults (never `undefined`, which would
 * make the inputs uncontrolled).
 */
export function reportToFormValues(
  report: PrefillableReport,
): WeeklyReportFormValues {
  return {
    week_start: dateValue(report.week_start),
    week_ending: dateValue(report.week_ending),
    overall_status:
      report.overall_status ?? weeklyReportFormDefaults.overall_status,
    escalation: enumValue(report.escalation, ["green", "red"] as const),
    schedule: enumValue(report.schedule, ["green", "yellow", "red"] as const),
    scope: enumValue(report.scope, ["green", "yellow", "red"] as const),
    budget_status: enumValue(report.budget_status, [
      "green",
      "yellow",
      "red",
    ] as const),
    cumulative_hours:
      report.cumulative_hours == null ? "" : String(report.cumulative_hours),
    // Legacy labels ("P2 — Design & Review") open as the canonical "Phase 2 — Design & Review" so
    // the Current Phase select shows a selectable option; anything unrecognised is kept as stored.
    current_phase:
      phaseNumber(report.current_phase) === null
        ? textValue(report.current_phase)
        : phaseLabel(report.current_phase),
    phase_p0_completed_at: dateValue(report.phase_p0_completed_at),
    phase_p1_completed_at: dateValue(report.phase_p1_completed_at),
    phase_p2_completed_at: dateValue(report.phase_p2_completed_at),
    phase_p3_completed_at: dateValue(report.phase_p3_completed_at),
    phase_p4_completed_at: dateValue(report.phase_p4_completed_at),
    project_health: textValue(report.project_health),
    planned_vs_actual: textValue(report.planned_vs_actual),
    team_did_this_week: textValue(report.team_did_this_week),
    team_will_do_next: textValue(report.team_will_do_next),
    risks: textValue(report.risks),
    decisions: textValue(report.decisions),
  };
}
